import { useEffect } from 'react';
import { useAuthStore, selectHasIdentity } from '../store/authStore';
import { wsClient } from '../services/websocket';
import { createDeviceCryptoKeys } from '../crypto/ed25519Provider';
import { base64ToBytes } from '../utils/encoding';

/**
 * Automatically connects WebSocket for temporary memberships when the device
 * does not have any full-access identities.
 *
 * If the device has a full identity, its regular WS connection logic takes over.
 * Should be called once in App or MainLayout.
 */
export function useTemporaryMembershipAutoConnect() {
  const temporaryMemberships = useAuthStore((state) => state.temporaryMemberships);
  const hasIdentity = useAuthStore(selectHasIdentity);

  useEffect(() => {
    if (hasIdentity) return; // regular WS connection handles this
    const memberships = Object.values(temporaryMemberships);
    if (memberships.length === 0) return;

    // Connect to the first membership's server (WS client handles one connection at a time)
    const membership = memberships[0];
    if (wsClient.isTemporaryMode()) return;

    const snapshot = wsClient.getServerConnectionSnapshot(membership.serverUrl);
    if (snapshot.state === 'connected') return;

    void (async () => {
      try {
        const { signingKey } = await createDeviceCryptoKeys(
          membership.privateKeyBytes,
          base64ToBytes(membership.publicKey.value)
        );
        await wsClient.connectTemporary({
          serverUrl: membership.serverUrl,
          deviceId: membership.deviceId,
          signingKey,
        });
      } catch {
        // Will retry on next effect run
      }
    })();
  }, [temporaryMemberships, hasIdentity]);
}
